<template>
  <CheckoutStep>
    <template #header>
      <Stepper :has-bouygues-steps="hasSelectedMobilePlan(items)" />
    </template>
    <template #aside>
      <CheckoutSummary
        :discount
        :discount-token
        :items="getAvailableItems(items)"
        :selected-payment-method="paymentMethod"
        :summary
        :swapOffer
        with-extended-legal
      />
    </template>

    <div aria-live="polite" role="status">
      <AddressConfirmation
        :address-list
        :items
        :last-collection-point
        :loading
        :swap-offer
        @update="handleUpdate"
      />
    </div>
  </CheckoutStep>
</template>

<script setup lang="ts">
import { onMounted } from 'vue'

import type { BuybackOffer } from '@backmarket/http-api'
import type { PaymentMethod } from '@backmarket/http-api/src/api-specs-payment/payment/payment.types'
import type { ApiSpecs } from '@backmarket/nuxt-module-http-v2/specs'

import CheckoutStep from '~/scopes/checkout/components/CheckoutStep/CheckoutStep.vue'
import CheckoutSummary from '~/scopes/checkout/components/CheckoutSummary/CheckoutSummary.vue'
import { useRedirectIfAddressNeedsBouyguesNormalization } from '~/scopes/checkout/composables/redirects/useRedirectIfAddressNeedsBouyguesNormalization'
import { useRedirectIfCartEmpty } from '~/scopes/checkout/composables/redirects/useRedirectIfCartEmpty'
import { useRedirectIfMissingShippingInformation } from '~/scopes/checkout/composables/redirects/useRedirectIfMissingShippingInformation'
import { useCheckoutTracking } from '~/scopes/checkout/composables/useCheckoutTracking'
import { CHECKOUT } from '~/scopes/checkout/routes-names'
import type { Discount } from '~/scopes/checkout/types/discount'
import { getAvailableItems } from '~/scopes/checkout/utils/cart'
import { hasSelectedMobilePlan } from '~/scopes/checkout/utils/hasSelectedMobilePlan'

import Stepper from '../../components/Stepper/Stepper.vue'

import AddressConfirmation from './components/AddressConfirmation/AddressConfirmation.vue'

const props = defineProps<{
  loading: boolean
  items: ApiSpecs['/bm/cart']['GET']['response']['cartItems']
  addressList: ApiSpecs['/bm/cart']['GET']['response']['addressList']
  billableCountries: ApiSpecs['/bm/cart']['GET']['response']['billableCountries']
  shippableCountries: ApiSpecs['/bm/cart']['GET']['response']['shippableCountries']
  lastCollectionPoint: ApiSpecs['/bm/cart']['GET']['response']['lastCollectionPoint']
  summary: ApiSpecs['/bm/cart']['GET']['response']['summary']
  swapOffer: BuybackOffer | null
  discount: Discount
  discountToken: string
  paymentMethod: PaymentMethod | null
}>()

const emit = defineEmits<{
  (e: 'update', callback?: () => void): void
}>()

const { trackFunnel } = useCheckoutTracking()
const { redirectIfCartEmpty } = useRedirectIfCartEmpty('ADDRESS_CONFIRMATION')
const { redirectIfAddressNeedsBouyguesNormalization } =
  useRedirectIfAddressNeedsBouyguesNormalization()
const { redirectIfMissingShippingInformation } =
  useRedirectIfMissingShippingInformation('ADDRESS_CONFIRMATION')

onMounted(async () => {
  await redirectIfCartEmpty(props.items, CHECKOUT.CART)
  await redirectIfMissingShippingInformation(
    props.addressList,
    props.shippableCountries,
  )
  if (hasSelectedMobilePlan(props.items)) {
    await redirectIfAddressNeedsBouyguesNormalization(props.addressList)
  }

  trackFunnel(props.items, props.swapOffer)
})

async function handleUpdate(callback?: () => void) {
  emit('update', callback)
}
</script>
